import { useChatStore } from "@stores/chatStore";
import { useChatStream } from "./useChatStream";
import { useSession } from "./useSession";

/**
 * The chat hook — composes the session lifecycle (`useSession`) and
 * the turn pipeline (`useChatStream`) into the single surface most
 * consumers want, plus the per-field store subscriptions needed for
 * the consumer's render path.
 *
 * The actual logic lives in:
 *   - `useSession` — load/start/clear/loadOlder + project-change
 *     reaction.
 *   - `useChatStream` — sendMessage / stopStreaming + the SSE pipeline.
 *   - `chatTurnRefs` — module-level abort + stop singletons shared
 *     between the two.
 */
export function useChat() {
  // Per-field subscriptions — bulk destructure of a Zustand store
  // resubscribes the hook to *every* field, so unrelated mutations
  // (activity ticks, transient status flashes, oldestTimestamp
  // refreshes) re-render every consumer of useChat. The fields below
  // are the ones whose changes the consumer actually needs to react to.
  const messages = useChatStore((s) => s.messages);
  const sessionId = useChatStore((s) => s.sessionId);
  const sessionTitle = useChatStore((s) => s.sessionTitle);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamingContent = useChatStore((s) => s.streamingContent);
  const activity = useChatStore((s) => s.activity);
  const transientStatus = useChatStore((s) => s.transientStatus);
  const hasMoreOlder = useChatStore((s) => s.hasMoreOlder);
  const oldestTimestamp = useChatStore((s) => s.oldestTimestamp);
  const isLoadingOlder = useChatStore((s) => s.isLoadingOlder);
  const isHydrating = useChatStore((s) => s.isHydrating);

  const session = useSession();
  const stream = useChatStream();

  return {
    messages,
    sessionId,
    sessionTitle,
    isStreaming,
    streamingContent,
    activity,
    transientStatus,
    hasMoreOlder,
    oldestTimestamp,
    isLoadingOlder,
    isHydrating,
    sendMessage: stream.sendMessage,
    stopStreaming: stream.stopStreaming,
    loadSessionById: session.loadSessionById,
    loadOlderMessages: session.loadOlderMessages,
    clearConversation: session.clearConversation,
    startNewSession: session.startNewSession,
  };
}
